/**
 * Shared cryptographic primitives for the trust-layer.
 *
 * This module is consumed by both verify.ts (online React path) and by the
 * generated offline-verifier HTML (which inlines the compiled output). Keeping
 * a single implementation guarantees the two paths are always in agreement.
 *
 * Design constraints:
 *   - Zero external dependencies — pure WebCrypto SubtleCrypto.
 *   - No `jose` library: JWS ES256 is verified by splitting the compact token
 *     and calling SubtleCrypto.verify() directly.
 *   - No Node built-ins — runs in any modern browser or Node 20+.
 *   - All inputs/outputs use ArrayBuffer-backed Uint8Array so WebCrypto is
 *     never passed ArrayBufferLike (SharedArrayBuffer) slices.
 *
 * Canonical contract: gg-app CONTRACT.md (Lane B).
 */

// ---------------------------------------------------------------------------
// Base64 / hex utilities
// ---------------------------------------------------------------------------

/** Decode a base64url string to a Uint8Array<ArrayBuffer>. */
export function decodeBase64url(b64url: string): Uint8Array<ArrayBuffer> {
  const padded = b64url + '==='.slice(0, (4 - (b64url.length % 4)) % 4);
  const b64 = padded.replace(/-/g, '+').replace(/_/g, '/');
  const binary = atob(b64);
  const buf = new ArrayBuffer(binary.length);
  const bytes = new Uint8Array(buf);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes;
}

/** Decode a standard base64 (non-URL-safe) string to Uint8Array<ArrayBuffer>. */
export function decodeBase64(b64: string): Uint8Array<ArrayBuffer> {
  const binary = atob(b64);
  const buf = new ArrayBuffer(binary.length);
  const bytes = new Uint8Array(buf);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes;
}

/** Encode a Uint8Array as base64url (no padding). */
export function encodeBase64url(bytes: Uint8Array): string {
  let binary = '';
  for (let i = 0; i < bytes.length; i++) binary += String.fromCharCode(bytes[i]!);
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

/** Encode a Uint8Array as standard base64 (with padding). */
export function encodeBase64(bytes: Uint8Array): string {
  let binary = '';
  for (let i = 0; i < bytes.length; i++) binary += String.fromCharCode(bytes[i]!);
  return btoa(binary);
}

/** Return the lowercase hex encoding of an ArrayBuffer. */
export function toHex(buffer: ArrayBuffer): string {
  return Array.from(new Uint8Array(buffer))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
}

// ---------------------------------------------------------------------------
// Hashing
// ---------------------------------------------------------------------------

/** Compute SHA-256 of bytes and return as a lowercase hex string. */
export async function sha256Hex(bytes: Uint8Array<ArrayBuffer>): Promise<string> {
  const digest = await crypto.subtle.digest('SHA-256', bytes);
  return toHex(digest);
}

/** Compute SHA-256 of bytes and return the raw ArrayBuffer. */
export async function sha256Raw(bytes: Uint8Array<ArrayBuffer>): Promise<ArrayBuffer> {
  return crypto.subtle.digest('SHA-256', bytes);
}

// ---------------------------------------------------------------------------
// Canonical JSON
// ---------------------------------------------------------------------------

/**
 * Canonical JSON: recursively sort object keys, no insignificant whitespace.
 *
 * Matches serde_json's default serialization over a sorted BTreeMap on the
 * Rust side. This is the canonical form used for entryHash derivation and
 * attestation challenge computation.
 *
 * Cross-language caveats (also pinned in gg-app CONTRACT.md): agreement with
 * serde_json holds only for ASCII object keys (JS sorts by UTF-16 code unit,
 * Rust BTreeMap by UTF-8 byte) and integer numbers (float formatting can
 * diverge). The wire format satisfies both today; keep it that way.
 *
 * Keys with `undefined` values are omitted, matching JSON.stringify object
 * semantics — without this, an undefined value would emit the literal text
 * `undefined` and silently corrupt the canonical form.
 */
export function canonicalJSON(value: unknown): string {
  if (value === null || typeof value !== 'object') {
    return JSON.stringify(value);
  }
  if (Array.isArray(value)) {
    return '[' + value.map(canonicalJSON).join(',') + ']';
  }
  const record = value as Record<string, unknown>;
  const sorted = Object.keys(record)
    .filter((k) => record[k] !== undefined)
    .sort()
    .map((k) => JSON.stringify(k) + ':' + canonicalJSON(record[k]));
  return '{' + sorted.join(',') + '}';
}

// ---------------------------------------------------------------------------
// DER ECDSA → raw r‖s conversion
// ---------------------------------------------------------------------------

/**
 * Convert a DER-encoded ECDSA P-256 signature to raw r‖s (64 bytes).
 *
 * WebAuthn authenticators emit DER-encoded signatures. WebCrypto's ECDSA
 * `verify` with the 'raw' import format expects 64 bytes (r‖s, 32 each).
 *
 * DER ECDSA structure:
 *   SEQUENCE { INTEGER r, INTEGER s }
 *   30 <len> 02 <rlen> <r-bytes> 02 <slen> <s-bytes>
 * Each INTEGER may have a leading 0x00 padding byte if the high bit is set.
 *
 * Bounds-checking: rejects len=0 or len>33 per INTEGER (P-256 scalars are
 * at most 32 bytes plus one optional 0x00 pad byte).
 */
export function derEcdsaToRaw(der: Uint8Array<ArrayBuffer>): Uint8Array<ArrayBuffer> {
  let offset = 0;

  if (der[offset] !== 0x30) throw new Error('DER: expected SEQUENCE (0x30)');
  offset++;

  // Sequence length: short or long form.
  let seqLen = der[offset++]!;
  if (seqLen & 0x80) {
    const numLenBytes = seqLen & 0x7f;
    seqLen = 0;
    for (let i = 0; i < numLenBytes; i++) seqLen = (seqLen << 8) | der[offset++]!;
  }
  // Validate sequence fits in the buffer.
  if (offset + seqLen > der.length) {
    throw new Error('DER: SEQUENCE length exceeds buffer');
  }
  const seqEnd = offset + seqLen;

  function readInt(): Uint8Array<ArrayBuffer> {
    if (offset >= seqEnd) throw new Error('DER: INTEGER read past sequence boundary');
    if (der[offset] !== 0x02) throw new Error('DER: expected INTEGER (0x02)');
    offset++;
    const len = der[offset++]!;
    // P-256: r and s are 32-byte scalars; with leading 0x00 pad: max 33.
    if (len === 0 || len > 33) throw new Error(`DER: INTEGER length ${len} invalid for P-256`);
    if (offset + len > seqEnd) throw new Error('DER: INTEGER length exceeds SEQUENCE boundary');

    let start = offset;
    let actualLen = len;
    // Strip leading 0x00 padding bytes.
    while (actualLen > 32 && der[start] === 0x00) {
      start++;
      actualLen--;
    }
    if (actualLen > 32) throw new Error(`DER: INTEGER actual length ${actualLen} exceeds 32`);

    const out = new ArrayBuffer(32);
    const view = new Uint8Array(out);
    // Right-align into 32 bytes (pad with leading zeros if short).
    view.set(der.subarray(start, start + actualLen), 32 - actualLen);
    offset += len;
    return view;
  }

  const r = readInt();
  const s = readInt();

  // Strict DER: nothing may follow s — trailing bytes mean a malformed
  // (or deliberately padded) encoding and are rejected.
  if (offset !== seqEnd || seqEnd !== der.length) {
    throw new Error('DER: trailing bytes after SEQUENCE');
  }

  const raw = new ArrayBuffer(64);
  const rawView = new Uint8Array(raw);
  rawView.set(new Uint8Array(r), 0);
  rawView.set(new Uint8Array(s), 32);
  return rawView;
}

// ---------------------------------------------------------------------------
// SPKI PEM parsing (for the seal public key)
// ---------------------------------------------------------------------------

/**
 * Parse an SPKI PEM string to a CryptoKey usable for ES256 ECDSA verification.
 *
 * The PEM armor (-----BEGIN/END PUBLIC KEY-----) and whitespace are stripped
 * before decoding, so the key works whether or not it has embedded newlines.
 */
export async function importSpkiPemForVerify(pem: string): Promise<CryptoKey> {
  const b64 = pem
    .replace(/-----BEGIN PUBLIC KEY-----/g, '')
    .replace(/-----END PUBLIC KEY-----/g, '')
    .replace(/\s+/g, '');
  const der = decodeBase64(b64);
  return crypto.subtle.importKey('spki', der, { name: 'ECDSA', namedCurve: 'P-256' }, false, [
    'verify',
  ]);
}

/**
 * SHA-256 fingerprint of an SPKI PEM public key's DER bytes, as
 * colon-separated lowercase hex (e.g. "ab:cd:…"). This is the value
 * published at absolutepinks.com/.well-known/seal-key-fingerprint.txt —
 * surfacing it next to verification results is what makes the
 * "confirm the key out-of-band" instruction actionable.
 */
export async function spkiPemFingerprint(pem: string): Promise<string> {
  const b64 = pem
    .replace(/-----BEGIN PUBLIC KEY-----/g, '')
    .replace(/-----END PUBLIC KEY-----/g, '')
    .replace(/\s+/g, '');
  const der = decodeBase64(b64);
  // An empty PEM would otherwise yield the well-known SHA-256-of-empty
  // fingerprint (e3:b0:c4:...) — misleading, must fail instead.
  if (der.length === 0) throw new Error('spkiPemFingerprint: empty PEM body');
  const hex = toHex(await crypto.subtle.digest('SHA-256', der));
  return hex.match(/.{2}/g)!.join(':');
}

/**
 * Import an attester's SPKI DER public key (standard base64, not url-safe).
 *
 * The canonical contract stores attesterPublicKey as SPKI DER base64 (standard).
 */
export async function importSpkiBase64ForVerify(spkiBase64: string): Promise<CryptoKey> {
  const derBytes = decodeBase64(spkiBase64);
  return crypto.subtle.importKey('spki', derBytes, { name: 'ECDSA', namedCurve: 'P-256' }, false, [
    'verify',
  ]);
}

// ---------------------------------------------------------------------------
// JWS ES256 verification (no jose dependency)
// ---------------------------------------------------------------------------

/**
 * Verify a compact JWS ES256 token against an SPKI PEM public key.
 *
 * The JWS compact serialization is: header.payload.signature (base64url parts).
 * The signature covers the ASCII bytes of "header.payload" (the two base64url
 * parts joined by a literal dot — NOT the decoded content).
 * The raw r‖s (64 bytes) signature is what Cloud KMS emits after DER→raw
 * conversion. SubtleCrypto's ECDSA verify expects raw format.
 *
 * Returns the decoded payload as a plain object, or throws on any failure.
 */
export async function verifyJwsEs256(
  jws: string,
  publicKey: CryptoKey,
): Promise<Record<string, unknown>> {
  const parts = jws.split('.');
  if (parts.length !== 3) throw new Error('JWS: expected 3 compact parts');
  const [headerB64, payloadB64, sigB64] = parts as [string, string, string];

  // Validate the protected header. The pinned P-256 key already blocks
  // alg-confusion, but a wrong-alg token must fail loudly, not verify by
  // coincidence of key type. Unknown `crit` extensions must be rejected
  // per RFC 7515 §4.1.11 (we support none).
  let header: unknown;
  try {
    header = JSON.parse(new TextDecoder().decode(decodeBase64url(headerB64)));
  } catch {
    throw new Error('JWS: protected header is not valid JSON');
  }
  if (typeof header !== 'object' || header === null) {
    throw new Error('JWS: protected header must be a JSON object');
  }
  const headerObj = header as Record<string, unknown>;
  if (headerObj['alg'] !== 'ES256') {
    throw new Error(`JWS: alg must be "ES256", got "${String(headerObj['alg'])}"`);
  }
  if (headerObj['crit'] !== undefined) {
    throw new Error('JWS: crit header extensions are not supported');
  }

  // The signed message is the literal ASCII bytes of "header.payload".
  const signingInput = `${headerB64}.${payloadB64}`;
  const signingBytes = new TextEncoder().encode(signingInput) as Uint8Array<ArrayBuffer>;

  // Signature is raw r‖s (64 bytes) in base64url.
  const sigBytes = decodeBase64url(sigB64);
  if (sigBytes.length !== 64)
    throw new Error(`JWS: signature must be 64 bytes (raw r‖s), got ${sigBytes.length}`);

  const valid = await crypto.subtle.verify(
    { name: 'ECDSA', hash: 'SHA-256' },
    publicKey,
    sigBytes,
    signingBytes,
  );
  if (!valid) throw new Error('JWS: signature verification failed');

  // Decode and parse the payload.
  const payloadBytes = decodeBase64url(payloadB64);
  const payloadText = new TextDecoder().decode(payloadBytes);
  return JSON.parse(payloadText) as Record<string, unknown>;
}

// ---------------------------------------------------------------------------
// Attestation challenge
// ---------------------------------------------------------------------------

/**
 * Compute the challenge for an attestation event per the canonical contract.
 *
 * challenge = SHA-256(canonicalJSON({vehicleId, headHash, statement, createdAt}))
 *
 * Returns the raw 32-byte digest encoded as base64url — this is the value that
 * must appear in clientDataJSON.challenge (the authenticator stores raw bytes
 * as base64url, not the hex string).
 *
 * createdAt is hashed as the exact wire string (RFC 3339 with Z suffix).
 * No Z→+00:00 normalization is applied.
 */
export async function computeAttestationChallenge(attestation: {
  vehicleId: string;
  headHash: string;
  statement: string;
  createdAt: string;
}): Promise<string> {
  const canonical = canonicalJSON({
    vehicleId: attestation.vehicleId,
    headHash: attestation.headHash,
    statement: attestation.statement,
    createdAt: attestation.createdAt,
  });
  const encoded = new TextEncoder().encode(canonical) as Uint8Array<ArrayBuffer>;
  const digest = await crypto.subtle.digest('SHA-256', encoded);
  return encodeBase64url(new Uint8Array(digest));
}

// ---------------------------------------------------------------------------
// WebAuthn clientDataJSON validation helpers
// ---------------------------------------------------------------------------

/**
 * Parse and validate the WebAuthn clientDataJSON.
 *
 * Checks:
 *   - type === "webauthn.get"
 *   - origin is in the provided allowlist
 *   - challenge field is present (returned for comparison)
 *
 * Throws with a descriptive message on any failure.
 */
export function parseClientDataJSON(
  clientDataJSON: Uint8Array,
  allowedOrigins: string[],
): { challenge: string; type: string; origin: string } {
  const text = new TextDecoder().decode(clientDataJSON);
  let parsed: unknown;
  try {
    parsed = JSON.parse(text);
  } catch {
    throw new Error('clientDataJSON is not valid JSON');
  }

  if (typeof parsed !== 'object' || parsed === null) {
    throw new Error('clientDataJSON must be a JSON object');
  }

  const obj = parsed as Record<string, unknown>;

  const type = obj['type'];
  if (type !== 'webauthn.get') {
    throw new Error(`clientDataJSON.type must be "webauthn.get", got "${String(type)}"`);
  }

  const challenge = obj['challenge'];
  if (typeof challenge !== 'string' || challenge.length === 0) {
    throw new Error('clientDataJSON.challenge missing or empty');
  }

  const origin = obj['origin'];
  if (typeof origin !== 'string') {
    throw new Error('clientDataJSON.origin missing');
  }
  if (allowedOrigins.length > 0 && !allowedOrigins.includes(origin)) {
    throw new Error(
      `clientDataJSON.origin "${origin}" not in allowlist [${allowedOrigins.join(', ')}]`,
    );
  }

  return { challenge, type, origin };
}

/**
 * Validate authenticatorData flags and rpIdHash.
 *
 * Checks:
 *   - UV (user-verified) flag: byte 32, bit 0x04 — MUST be set.
 *   - UP (user-presence) flag: byte 32, bit 0x01 — MUST be set.
 *   - rpIdHash: bytes 0-31 — must equal SHA-256(rpId).
 *
 * Throws with a descriptive message on any failure.
 */
export async function validateAuthenticatorData(
  authData: Uint8Array<ArrayBuffer>,
  rpId: string,
): Promise<void> {
  if (authData.length < 37) {
    throw new Error(`authenticatorData too short: ${authData.length} bytes (min 37)`);
  }

  const flags = authData[32]!;

  if ((flags & 0x04) === 0) {
    throw new Error('userVerificationAbsent: UV flag (0x04) not set in authenticatorData');
  }

  if ((flags & 0x01) === 0) {
    throw new Error('userPresenceAbsent: UP flag (0x01) not set in authenticatorData');
  }

  const rpIdHashBytes = new TextEncoder().encode(rpId) as Uint8Array<ArrayBuffer>;
  const expectedRpIdHash = await crypto.subtle.digest('SHA-256', rpIdHashBytes);
  const storedRpIdHash = authData.subarray(0, 32);

  const expectedView = new Uint8Array(expectedRpIdHash);
  for (let i = 0; i < 32; i++) {
    if (storedRpIdHash[i] !== expectedView[i]) {
      throw new Error(
        `rpIdHash mismatch: authenticatorData[0..32] does not match SHA-256("${rpId}")`,
      );
    }
  }
}
